<script setup>
import {ref, computed, onMounted, onUnmounted, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {showToast, showLoadingToast, closeToast} from 'vant'
import {api} from '@/api/api'
import {openWxMiniPay, startPage} from '@/assets/js/base'

/**
 * 新人红包：对接后端 getNewRedPacket / receiveNewRedPacket
 * - 登录成功或进入首页/我的且已登录时拉取红包信息
 * - payStatus≠3 且 countdown 未过期：展示悬浮窗（未领取：领红包；已领取：去微信确认收款）
 * - 领取接口仅下发 packageInfo，到账需用户在微信内确认，通过 openWxMiniPay(packageInfo) 跳转
 * - 悬浮窗倒计时按秒递减，为 0 时关闭悬浮窗并同步关闭主弹窗
 * - 金额使用后端 packetAmount
 */

const route = useRoute()
const router = useRouter()

/** sessionStorage：本次会话是否已自动弹出过主弹窗 */
const KEY_MAIN_SHOWN_SESSION = 'newRedPacketMainShownSession'
/** sessionStorage：用户是否点关闭最小化到悬浮窗 */
const KEY_MINIMIZED = 'newRedPacketMinimized'

const showMainPopup = ref(false)
const showSuccessPopup = ref(false)
const showFloating = ref(false)
/** 打款成功提示弹窗：用于拦截重复确认收款时的稳定提示 */
const showPaidSuccessPopup = ref(false)
/** 新手任务是否有效（有任务数据且未过期） */
const newPeopleActivityAvailable = ref(false)

/** 后端是否允许展示红包模块 */
const redPacketEnabled = ref(false)
/** 是否已领取 */
const received = ref(false)
/** 是否绑定微信 */
const bindWechat = ref(true)
/**
 * 拉起微信确认收款所需的 packageInfo
 * 来源：getNewRedPacket.result.packageInfo 或 receiveNewRedPacket.result
 */
const packageInfo = ref('')
/**
 * 支付状态：1待支付 2支付中 3支付成功 4支付失败 -1支付异常
 * 仅 payStatus===3 视为到账完成，其余在倒计时未结束前可展示「待确认」悬浮入口
 */
const payStatus = ref(1)

const amount = ref('0.3')
/** 新手任务阶数与总奖励（底部任务卡片展示） */
const activityTaskCount = ref(5)
const activityTotalReward = ref('2.5')

/** 剩余秒数（后端倒计时字段是毫秒，这里换算为秒后本地递减） */
const countdownRemainingSeconds = ref(0)
let secondTimer = null
/** 防止并发拉取导致弹窗状态抖动 */
let isFetchingRedPacketInfo = false
/** 最近一次登录触发时间（用于忽略紧随其后的重复刷新事件） */
let lastLoginTriggerAt = 0

const claiming = ref(false)
/** 点击「前往微信确认收款」时的状态校验 loading，防止重复点击 */
const checkingWxConfirmStatus = ref(false)
/**
 * 是否已点击过「前往微信确认收款」按钮
 * 仅在该标记为 true 时，onPageResume 才会回源刷新支付状态
 */
const hasTriggeredWxMiniConfirm = ref(false)
/** 保存页面原有 onPageResume，避免覆盖其它页面逻辑 */
let previousOnPageResume = null

/** 是否已微信支付成功（到账完成） */
const isPaySuccess = computed(() => payStatus.value === 3)
/**
 * 未到账且倒计时仍有剩余：允许展示悬浮窗（与后端「活动未结束」对齐）
 */
const shouldShowFloatForPendingPay = computed(
    () => !isPaySuccess.value && countdownRemainingSeconds.value > 0
)

const shouldShowOnCurrentPage = computed(() => {
    const n = route.name
    return n === 'home' || n === 'mine' || n === 'Home' || n === 'Mine'
})

/** 仅在首页/我的且后端允许展示时，渲染红包相关 UI */
const canShowRedPacketUi = computed(() => redPacketEnabled.value && shouldShowOnCurrentPage.value)

/**
 * 将金额格式化为展示字符串（去掉多余小数 0）
 * @param {number|string} val 后端 packetAmount
 */
function formatPacketAmount(val) {
    const num = Number(val)
    if (!Number.isFinite(num)) return '0'
    const s = num.toFixed(2)
    return s.replace(/\.?0+$/, '')
}

/**
 * 主弹窗倒计时文案：仅展示「X小时XX分后失效」（不含秒）
 * @param {number} sec 剩余秒
 */
function formatCountdownHoursMinutes(sec) {
    if (sec <= 0) return '已失效'
    const h = Math.floor(sec / 3600)
    const m = Math.floor((sec % 3600) / 60)
    const pad = (n) => (n < 10 ? `0${n}` : String(n))
    return `${h}小时${pad(m)}分后失效`
}

const mainPopupCountdownText = computed(() =>
    formatCountdownHoursMinutes(countdownRemainingSeconds.value)
)

/** 停止本地秒级倒计时 */
function stopSecondTick() {
    if (secondTimer) {
        clearInterval(secondTimer)
        secondTimer = null
    }
}

/** 启动本地秒级倒计时（依赖 countdownRemainingSeconds 当前值） */
function startSecondTick() {
    stopSecondTick()
    if (countdownRemainingSeconds.value <= 0) return
    secondTimer = setInterval(() => {
        if (countdownRemainingSeconds.value <= 0) {
            stopSecondTick()
            onCountdownFinished()
            return
        }
        countdownRemainingSeconds.value -= 1
        if (countdownRemainingSeconds.value <= 0) {
            stopSecondTick()
            onCountdownFinished()
        }
    }, 1000)
}

/**
 * 倒计时结束：关闭悬浮窗与主弹窗，并重新拉取一次后端状态
 */
async function onCountdownFinished() {
    showFloating.value = false
    showMainPopup.value = false
    sessionStorage.removeItem(KEY_MINIMIZED)
    try {
        await fetchNewRedPacketInfo({afterLogin: false, silent: true})
    } catch {
        redPacketEnabled.value = false
    }
}

/**
 * 拉取新用户红包信息
 * @param {{ afterLogin?: boolean, silent?: boolean }} opts afterLogin=登录成功后的首次拉取；silent=失败时不 toast
 */
async function fetchNewRedPacketInfo(opts = {}) {
    // 请求互斥，避免短时间多次触发导致重复动画
    if (isFetchingRedPacketInfo) return
    isFetchingRedPacketInfo = true
    const {afterLogin = false, silent = false} = opts
    const token = localStorage.getItem('token')
    // 有 token 即拉取：登录态可能在非首页路由，仍需先同步后端红包状态
    if (!token) {
        isFetchingRedPacketInfo = false
        return
    }
    try {
        const res = await api.getNewRedPacket({})
        if (res.code !== 200 || !res.result) {
            if (!silent) showToast(res.message || '获取红包信息失败')
            redPacketEnabled.value = false
            showMainPopup.value = false
            showFloating.value = false
            showSuccessPopup.value = false
            stopSecondTick()
            return
        }
        const r = res.result
        if (!r.show) {
            redPacketEnabled.value = false
            showMainPopup.value = false
            showFloating.value = false
            showSuccessPopup.value = false
            stopSecondTick()

            return
        }

        redPacketEnabled.value = true
        received.value = !!r.received
        bindWechat.value = !!r.bingWechat
        packageInfo.value = typeof r.packageInfo === 'string' ? r.packageInfo : ''
        amount.value = formatPacketAmount(r.packetAmount)

        // 解析支付状态（缺省时：已领取按「支付中」对待，未领取按「待支付」）
        const psRaw = r.payStatus
        const psNum = psRaw != null && psRaw !== '' ? Number(psRaw) : NaN
        payStatus.value = Number.isFinite(psNum) ? psNum : received.value ? 2 : 1

        // 后端 countdownSecond 为毫秒，向上取整为秒，避免用户看到倒计时提前结束
        const countdownMs = Math.max(0, Number(r.countdownSecond) || 0)
        countdownRemainingSeconds.value = Math.ceil(countdownMs / 1000)
        startSecondTick()

        // 已领取：主弹窗关闭；未到账时在首页/我的展示悬浮入口（成功引导弹窗打开时不叠悬浮窗）
        if (received.value) {
            showMainPopup.value = false
            sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
            sessionStorage.removeItem(KEY_MINIMIZED)
            const needFloat =
                shouldShowOnCurrentPage.value &&
                !isPaySuccess.value &&
                countdownRemainingSeconds.value > 0 &&
                !showSuccessPopup.value
            showFloating.value = needFloat
            return
        }

        if (afterLogin) {
            sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
            sessionStorage.removeItem(KEY_MINIMIZED)
        }

        // 非首页/我的：只同步数据，不弹出层（进入 Tab 后再由路由 watch 展示）
        if (!shouldShowOnCurrentPage.value) {
            showMainPopup.value = false
            showFloating.value = false
            return
        }

        const minimized = sessionStorage.getItem(KEY_MINIMIZED) === '1'
        const mainShown = sessionStorage.getItem(KEY_MAIN_SHOWN_SESSION) === '1'

        if (minimized) {
            showFloating.value = true
            showMainPopup.value = false
            return
        }

        if (afterLogin || !mainShown) {
            showMainPopup.value = true
            showFloating.value = false
            sessionStorage.setItem(KEY_MAIN_SHOWN_SESSION, '1')
            return
        }

        showMainPopup.value = false
        showFloating.value = false
    } catch (e) {
        if (!silent) showToast('网络异常，请稍后重试')
        console.error('getNewRedPacket', e)
    } finally {
        isFetchingRedPacketInfo = false
    }
}

/** 打开主弹窗（从悬浮窗进入时清除最小化标记） */
function openMainPopup() {
    if (!canShowRedPacketUi.value || received.value) return
    sessionStorage.removeItem(KEY_MINIMIZED)
    showMainPopup.value = true
    showFloating.value = false
}

/**
 * 领取红包：调用 receiveNewRedPacket
 * 成功返回的 result 即为 packageInfo；到账需在微信内确认，不停止本地倒计时
 */
async function claimRedPacket() {
    if (claiming.value || received.value || !redPacketEnabled.value) return
    claiming.value = true
    try {
        const res = await api.receiveNewRedPacket({})
        // 后端领取成功码可能为 200 或 0
        const ok = res.code === 200 || res.code === 0
        if (ok) {
            const pkg = typeof res.result === 'string' ? res.result : ''
            if (pkg) {
                packageInfo.value = pkg
            }
            showMainPopup.value = false
            received.value = true
            payStatus.value = 2
            sessionStorage.removeItem(KEY_MINIMIZED)
            sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
            setTimeout(() => {
                showSuccessPopup.value = true
                void fetchNewPeopleActivityStatus()
                const tip = bindWechat.value
                    ? '领取成功，请前往微信内确认收款'
                    : '领取成功，请先绑定微信后再到微信确认收款'
                showToast(tip)
            }, 200)
            // 同步后端倒计时与 payStatus（保持倒计时运行，不在此处 stopSecondTick）
            void fetchNewRedPacketInfo({afterLogin: false, silent: true})
        } else {
            showToast(res.message || '领取失败，请稍后重试')
        }
    } catch (e) {
        console.error('receiveNewRedPacket', e)
        showToast('网络异常，请稍后重试')
    } finally {
        claiming.value = false
    }
}

/**
 * 关闭弹窗并保留悬浮入口
 * - 未领取：写入最小化标记并展示悬浮窗
 * - 已领取待确认：展示「去微信确认」悬浮窗
 */
function dismissPopupToFloating() {
    showMainPopup.value = false
    showSuccessPopup.value = false

    if (!redPacketEnabled.value || countdownRemainingSeconds.value <= 0) {
        showFloating.value = false
        sessionStorage.removeItem(KEY_MINIMIZED)
        return
    }

    if (!received.value) {
        sessionStorage.setItem(KEY_MINIMIZED, '1')
        showFloating.value = true
        return
    }

    sessionStorage.removeItem(KEY_MINIMIZED)
    showFloating.value = payStatus.value !== 3
}

/** 关闭主弹窗并跳转新手任务页 */
function goNewPeopleActivity() {
    showPaidSuccessPopup.value = false
    dismissPopupToFloating()
    void api.upUserVisit({})
    startPage('newPeopleActivity')
}

/** 关闭主弹窗并最小化到悬浮窗 */
function closeMainToFloat() {
    if (!redPacketEnabled.value || received.value) return
    sessionStorage.setItem(KEY_MINIMIZED, '1')
    showMainPopup.value = false
    showFloating.value = true
}

function closeSuccessPopup() {
    dismissPopupToFloating()
}

function parseActivityRestTimeMs(result) {
    if (!result || typeof result !== 'object') return 0
    const raw = result.restTime ?? result.countDown
    const num = Number(raw)
    return Number.isFinite(num) && num > 0 ? num : 0
}

/** 拉取新手任务状态：有任务列表且倒计时未结束视为可用 */
async function fetchNewPeopleActivityStatus() {
    try {
        const res = await api.getActivityTask({})
        if (res?.code === 200 && res.result) {
            const list = Array.isArray(res.result.taskList) ? res.result.taskList : []
            const restMs = parseActivityRestTimeMs(res.result)
            newPeopleActivityAvailable.value = list.length > 0 && restMs > 0
            if (list.length > 0) {
                activityTaskCount.value = list.length
                const sum = list.reduce((acc, task) => acc + (Number(task?.amount) || 0), 0)
                if (sum > 0) {
                    activityTotalReward.value = formatPacketAmount(sum)
                }
            }
        } else {
            newPeopleActivityAvailable.value = false
        }
    } catch {
        newPeopleActivityAvailable.value = false
    }
}

const paidSuccessBtnText = computed(() =>
    newPeopleActivityAvailable.value ? '去完成新手任务' : '我知道了'
)

/** 关闭打款成功提示弹窗 */
function closePaidSuccessPopup() {
    showPaidSuccessPopup.value = false
    if (newPeopleActivityAvailable.value) {
        startPage('newPeopleActivity')
    }
}

/**
 * 唤起微信确认收款（原生 wxOpenBusiness）
 */
async function handleOpenWxMiniConfirm() {
    // 正在校验时不重复触发，避免重复请求与重复跳转
    if (checkingWxConfirmStatus.value) return
    const pkg = (packageInfo.value || '').trim()
    if (!pkg) {
        showToast('暂无可用的收款信息，请稍后重试或重新进入页面')
        return
    }
    checkingWxConfirmStatus.value = true
    showLoadingToast({
        message: '状态校验中...',
        forbidClick: true,
        duration: 0,
    })
    try {
        // 跳转前先拉取最新状态：若已到账，直接提示并关闭相关弹层
        const res = await api.getNewRedPacket({})
        if (res.code !== 200 || !res.result) {
            showToast(res.message || '状态校验失败，请稍后重试')
            return
        }
        const r = res.result
        const currentReceived = !!r.received
        const psRaw = r.payStatus
        const psNum = psRaw != null && psRaw !== '' ? Number(psRaw) : NaN
        const latestPayStatus = Number.isFinite(psNum) ? psNum : currentReceived ? 2 : 1

        if (latestPayStatus === 3) {
            payStatus.value = 3
            received.value = true
            hasTriggeredWxMiniConfirm.value = false
            showSuccessPopup.value = false
            showFloating.value = false
            showMainPopup.value = false
            // 使用独立弹窗提示打款成功，避免 toast 在弹层切换时一闪而过
            showPaidSuccessPopup.value = true
            void fetchNewPeopleActivityStatus()
            return
        }

        // 未到账则继续原流程，等待 onPageResume 后再次回源刷新
        hasTriggeredWxMiniConfirm.value = true
        openWxMiniPay(pkg)
    } catch (e) {
        console.error('getNewRedPacket before wx confirm', e)
        showToast('网络异常，请稍后重试')
    } finally {
        checkingWxConfirmStatus.value = false
        closeToast()
    }
}

/**
 * 原生 onResume 回调到 H5：仅在用户点过确认收款按钮后刷新红包状态
 * 若已支付成功（payStatus===3），自动关闭引导弹窗与悬浮窗
 */
async function onPageResume() {
    if (typeof previousOnPageResume === 'function') {
        previousOnPageResume()
    }
    if (!hasTriggeredWxMiniConfirm.value) return
    await fetchNewRedPacketInfo({afterLogin: false, silent: true})
    if (payStatus.value === 3) {
        showSuccessPopup.value = false
        showFloating.value = false
        hasTriggeredWxMiniConfirm.value = false
    }
}

/**
 * 点击悬浮窗：未领取进入主弹窗；已领取但未到账则打开引导弹窗以便跳转微信
 */
function openFromFloating() {
    if (!canShowRedPacketUi.value || !shouldShowFloatForPendingPay.value) return
    if (received.value) {
        showSuccessPopup.value = true
        return
    }
    openMainPopup()
}

/** 全局登录成功：清会话标记后拉接口并优先弹主窗 */
function onAuthStateChanged(event) {
    const detail = event?.detail || {}
    if (detail.type === 'login') {
        // 记录登录刷新时间，防止后续立即到来的重复刷新再次触发弹窗动画
        lastLoginTriggerAt = Date.now()
        setTimeout(() => {
            void fetchNewRedPacketInfo({afterLogin: true, silent: false})
        }, 600)
        return
    }
    if (detail.type === 'logout') {
        redPacketEnabled.value = false
        received.value = false
        payStatus.value = 1
        packageInfo.value = ''
        hasTriggeredWxMiniConfirm.value = false
        showMainPopup.value = false
        showFloating.value = false
        showSuccessPopup.value = false
        showPaidSuccessPopup.value = false
        newPeopleActivityAvailable.value = false
        stopSecondTick()
        sessionStorage.removeItem(KEY_MINIMIZED)
        sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
    }
}

function onNewcomerRewardRefresh() {
    // 登录后会连续触发多个刷新事件，这里短暂节流避免动画重复播放
    if (Date.now() - lastLoginTriggerAt < 1200) return
    if (localStorage.getItem('token')) {
        void fetchNewRedPacketInfo({afterLogin: false, silent: true})
    }
}

watch(
    () => route.name,
    () => {
        if (!shouldShowOnCurrentPage.value) {
            showMainPopup.value = false
            showSuccessPopup.value = false
            showFloating.value = false
            return
        }
        if (localStorage.getItem('token')) {
            void fetchNewRedPacketInfo({afterLogin: false, silent: true})
        }
    }
)

onMounted(() => {
    // 页面刷新初始化时清理会话弹出标记，确保刷新后可重新自动弹主红包
    sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
    // 挂载原生 onResume 回调：先执行页面既有回调，再刷新红包状态
    previousOnPageResume = typeof window.onPageResume === 'function' ? window.onPageResume : null
    window.onPageResume = onPageResume
    window.addEventListener('auth-state-changed', onAuthStateChanged)
    window.addEventListener('newcomer-reward-refresh', onNewcomerRewardRefresh)
    if (localStorage.getItem('token')) {
        void fetchNewRedPacketInfo({afterLogin: false, silent: true})
        void fetchNewPeopleActivityStatus()
    }
})

onUnmounted(() => {
    stopSecondTick()
    // 组件卸载时恢复原页面 onPageResume，避免影响其它页面逻辑
    if (window.onPageResume === onPageResume) {
        window.onPageResume = previousOnPageResume
    }
    window.removeEventListener('auth-state-changed', onAuthStateChanged)
    window.removeEventListener('newcomer-reward-refresh', onNewcomerRewardRefresh)
})

defineExpose({
    fetchNewRedPacketInfo,
    openMainPopup,
})
</script>

<template>
    <div class="newcomer-red-packet-wrapper">
        <!-- 主红包弹窗 - 浅色奶油主题，居中，不可点击蒙层关闭 -->
        <van-overlay
            :show="canShowRedPacketUi && showMainPopup && !showSuccessPopup"
            :z-index="3000"
            class="red-packet-overlay"
            @click="() => {}"
        >
            <div class="popup-container" @click.stop>
                <div class="red-packet-popup">
                    <div class="close-btn main-close-btn" @click="closeMainToFloat">✕</div>

                    <div class="popup-badge">新人专享</div>
                    <div class="main-title">恭喜获得新人注册红包</div>

                    <div class="amount-container">
                        <span class="amount-plus">+</span>
                        <span class="amount-value">{{ amount }}</span>
                        <span class="amount-unit">元</span>
                    </div>

                    <div v-if="countdownRemainingSeconds > 0" class="countdown-section">
                        <svg aria-hidden="true" class="countdown-icon" fill="none" viewBox="0 0 16 16">
                            <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.2"/>
                            <path d="M8 4.5V8l2.5 1.5" stroke="currentColor" stroke-linecap="round"
                                  stroke-linejoin="round" stroke-width="1.2"/>
                        </svg>
                        <span class="countdown-time">{{ mainPopupCountdownText }}</span>
                    </div>

                    <button :disabled="claiming" class="claim-btn" type="button" @click="claimRedPacket">
                        {{ claiming ? '领取中…' : '立即领取' }}
                    </button>

                    <div class="popup-divider"></div>

                    <div class="task-card" role="button" @click="goNewPeopleActivity">
                        <div class="task-card-icon">
                            <img alt="" src="@/assets/images/image-2.c3128bc7.png"/>
                        </div>
                        <div class="task-card-content">
                            <div class="task-card-title">
                                完成新手任务可得
                                <span class="task-card-bonus">+{{ activityTotalReward }}元</span>
                            </div>
                            <div class="task-card-desc">共 {{ activityTaskCount }} 阶 · 注册起 7 天有效</div>
                        </div>
                        <div class="task-card-link">继续赚 &gt;</div>
                    </div>
                </div>
            </div>
        </van-overlay>

        <!-- 领取成功引导弹窗：款项需在微信内确认，非即时到账 -->
        <!-- 成功态不依赖 redPacketEnabled，避免领取后接口 show=false 导致弹窗闪退 -->
        <van-overlay
            :show="shouldShowOnCurrentPage && showSuccessPopup"
            :z-index="3100"
            class="success-overlay"
            @click="() => {}"
        >
            <div class="popup-container" @click.stop>
                <div class="success-popup">
                    <div class="close-btn main-close-btn" @click="closeSuccessPopup">✕</div>

                    <div aria-hidden="true" class="success-icon-wrap">
                        <div class="success-icon">
                            <svg class="success-icon-svg" role="img" viewBox="0 0 64 64">
                                <circle class="success-icon-circle" cx="32" cy="32" r="30"/>
                                <path class="success-icon-check" d="M20 33L28 41L45 24"/>
                            </svg>
                        </div>
                    </div>

                    <div class="success-title">领取成功</div>

                    <div class="success-amount-container">
                        <span class="success-amount-plus">+</span>
                        <span class="success-amount-value">{{ amount }}</span>
                        <span class="success-amount-unit">元</span>
                    </div>

                    <div class="success-desc">
                        微信确认后，奖励实时到账
                        <template v-if="!bindWechat">
                            <br>当前未绑定微信，请先完成绑定后再前往微信确认
                        </template>
                    </div>

                    <button
                        :disabled="checkingWxConfirmStatus"
                        class="success-confirm-btn"
                        type="button"
                        @click="handleOpenWxMiniConfirm"
                    >
                        {{ checkingWxConfirmStatus ? '状态校验中…' : '前往微信确认' }}
                    </button>

                    <div class="popup-divider"></div>

                    <div class="task-card" role="button" @click="goNewPeopleActivity">
                        <div class="task-card-icon">
                            <img alt="" src="@/assets/images/image-2.c3128bc7.png"/>
                        </div>
                        <div class="task-card-content">
                            <div class="task-card-title">
                                完成新手任务可得
                                <span class="task-card-bonus">+{{ activityTotalReward }}元</span>
                            </div>
                            <div class="task-card-desc">共 {{ activityTaskCount }} 阶 · 注册起 7 天有效</div>
                        </div>
                        <div class="task-card-link">继续赚 &gt;</div>
                    </div>
                </div>
            </div>
        </van-overlay>

        <!-- 打款成功提示弹窗：用于提示已到账，避免重复确认收款 -->
        <van-overlay
            :show="shouldShowOnCurrentPage && showPaidSuccessPopup"
            :z-index="3200"
            class="paid-success-overlay"
            @click="() => {}"
        >
            <div class="popup-container" @click.stop>
                <div class="paid-success-popup">
                    <div class="close-btn main-close-btn" @click="closePaidSuccessPopup">✕</div>

                    <div aria-hidden="true" class="paid-success-icon-wrap">
                        <div class="paid-success-icon">
                            <svg class="paid-success-icon-svg" role="img" viewBox="0 0 64 64">
                                <circle class="paid-success-icon-circle" cx="32" cy="32" r="30"/>
                                <path class="paid-success-icon-check" d="M20 33L28 41L45 24"/>
                            </svg>
                        </div>
                    </div>

                    <div class="paid-success-title">打款成功</div>

                    <div class="paid-success-amount-container">
                        <span class="paid-success-amount-plus">+</span>
                        <span class="paid-success-amount-value">{{ amount }}</span>
                        <span class="paid-success-amount-unit">元</span>
                    </div>

                    <div class="paid-success-desc">奖励已发放至微信零钱，可查看明细</div>

                    <button class="paid-success-btn" type="button" @click="closePaidSuccessPopup">
                        {{ paidSuccessBtnText }}
                    </button>

                    <template v-if="newPeopleActivityAvailable">
                        <div class="popup-divider"></div>

                        <div class="task-card" role="button" @click="goNewPeopleActivity">
                            <div class="task-card-icon">
                                <img alt="" src="@/assets/images/image-2.c3128bc7.png"/>
                            </div>
                            <div class="task-card-content">
                                <div class="task-card-title">
                                    完成新手任务可得
                                    <span class="task-card-bonus">+{{ activityTotalReward }}元</span>
                                </div>
                                <div class="task-card-desc">共 {{ activityTaskCount }} 阶 · 注册起 7 天有效</div>
                            </div>
                            <div class="task-card-link">继续赚 &gt;</div>
                        </div>
                    </template>
                </div>
            </div>
        </van-overlay>

        <!-- 悬浮窗：未领取=领红包；已领取待确认=去微信（payStatus≠3 且倒计时未结束） -->
        <div
            v-if="canShowRedPacketUi && showFloating && shouldShowFloatForPendingPay && !showSuccessPopup"
            class="floating-red-packet"
            @click="openFromFloating"
        >
            <img
                alt="新人红包"
                class="float-packet-img"
                src="@/assets/images/newhongbao.png"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
/* 新人红包UI样式 - 符合移动端375px基准 */
.newcomer-red-packet-wrapper {
    position: relative;
    z-index: 2000;
}

/* 蒙层样式：使用 Vant 默认蒙层，不自定义背景色 */
.red-packet-overlay,
.success-overlay,
.paid-success-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
}

/* 通用弹窗容器 */
.popup-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: auto;
    min-height: 100%;
    padding: 12px;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
}

/* 主红包弹窗 - 浅色奶油主题 */
.red-packet-popup {
    background: #fff9f1;
    border-radius: 24px;
    width: 100%;
    max-width: 272px;
    padding: 28px 15px;
    position: relative;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
    color: #333;
    text-align: center;
    overflow: hidden;
    animation: popupBounce 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes popupBounce {
    0% {
        transform: scale(0.6);
        opacity: 0;
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

/* 关闭按钮 */
.close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 24px;
    height: 24px;
    background: rgba(0, 0, 0, 0.06);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #999;
    cursor: pointer;
    z-index: 10;
    border: none;
    transition: all 0.2s;

    &:active {
        background: rgba(0, 0, 0, 0.1);
        transform: scale(0.9);
    }
}

.main-close-btn {
    top: 14px;
    right: 14px;
    width: 26px;
    height: 26px;
    font-size: 15px;
    color: #bbb;
    background: #f2f2f2;
}

.success-close {
    background: #f2f2f2;
    border: none;
    color: #bbb;
}

.popup-badge {
    display: inline-block;
    background: #ff5a33;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 14px;
    border-radius: 20px;
    margin-bottom: 12px;
    letter-spacing: 0.5px;
}

/* 主标题 */
.main-title {
    font-size: 17px;
    font-weight: 700;
    margin-bottom: 16px;
    color: #333;
    line-height: 1.4;
}

/* 金额展示 */
.amount-container {
    display: flex;
    align-items: baseline;
    justify-content: center;
    margin: 0 0 10px;
    font-weight: 700;
    color: #ff5a33;
}

.amount-plus {
    font-size: 22px;
    margin-right: 2px;
    font-weight: 600;
}

.amount-value {
    font-size: 48px;
    line-height: 1;
    color: #ff5a33;
    font-weight: 700;
    letter-spacing: -1px;
}

.amount-unit {
    font-size: 18px;
    margin-left: 2px;
    font-weight: 600;
}

/* 倒计时 */
.countdown-section {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 0 20px;
    color: #999;
}

.countdown-icon {
    width: 14px;
    height: 14px;
    margin-right: 4px;
    flex-shrink: 0;
}

.countdown-time {
    font-size: 13px;
    font-weight: 400;
    color: #999;
}

/* 领取按钮 */
.claim-btn {
    width: 100%;
    height: 48px;
    background: #ff5a33;
    color: #fff;
    border: none;
    border-radius: 50px;
    font-size: 17px;
    font-weight: 700;
    box-shadow: 0 8px 20px rgba(255, 90, 51, 0.35);
    cursor: pointer;
    transition: all 0.2s;

    &:active {
        transform: scale(0.96);
        box-shadow: 0 4px 12px rgba(255, 90, 51, 0.4);
    }

    &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }
}

.popup-divider {
    height: 1px;
    background: #f0ebe3;
    margin: 20px 0 16px;
}

.task-card {
    display: flex;
    align-items: center;
    background: #fff3e8;
    border-radius: 14px;
    padding: 12px 10px;
    text-align: left;
    cursor: pointer;

    &:active {
        opacity: 0.85;
    }
}

.task-card-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 90, 51, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 5px;

    img {
        width: 22px;
        height: 22px;
        object-fit: contain;
    }
}

.task-card-content {
    flex: 1;
    min-width: 0;
}

.task-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    line-height: 1.4;
    margin-bottom: 2px;
}

.task-card-bonus {
    color: #ff5a33;
    font-weight: 700;
}

.task-card-desc {
    font-size: 11px;
    color: #999;
    line-height: 1.3;
}

.task-card-link {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 600;
    color: #ff5a33;
    margin-left: 6px;
    white-space: nowrap;
}

/* 领取成功弹窗 */
.success-popup {
    background: #fff;
    border-radius: 24px;
    width: 100%;
    max-width: 272px;
    padding: 28px 15px;
    text-align: center;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
    position: relative;
    animation: successPop 0.4s ease;
}

.success-icon-wrap {
    width: 80px;
    height: 80px;
    margin: 0 auto 16px;
    background: rgba(57, 181, 74, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
}

.success-icon {
    width: 70px;
    height: 70px;
    margin: 0 auto;
    animation: successCheck 0.6s ease;
}

.success-icon-svg {
    width: 100%;
    height: 100%;
    display: block;
}

.success-icon-halo {
    fill: rgba(57, 181, 74, 0.15);
}

.success-icon-circle {
    fill: #39b54a;
}

.success-icon-check {
    fill: none;
    stroke: #fff;
    stroke-width: 5;
    stroke-linecap: round;
    stroke-linejoin: round;
}

@keyframes successCheck {
    0% {
        transform: scale(0.5) rotate(-20deg);
    }
    60% {
        transform: scale(1.15) rotate(10deg);
    }
    100% {
        transform: scale(1) rotate(0);
    }
}

.success-title {
    font-size: 18px;
    font-weight: 700;
    color: #333;
    margin-bottom: 12px;
}

.success-amount-container {
    display: flex;
    align-items: baseline;
    justify-content: center;
    margin-bottom: 8px;
    color: #39b54a;
    font-weight: 700;
}

.success-amount-plus {
    font-size: 22px;
    margin-right: 2px;
    font-weight: 600;
}

.success-amount-value {
    font-size: 44px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: -1px;
}

.success-amount-unit {
    font-size: 18px;
    margin-left: 2px;
    font-weight: 600;
}

.success-desc {
    color: #999;
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 20px;
}

.success-confirm-btn {
    width: 100%;
    height: 48px;
    background: #39b54a;
    color: #fff;
    border: none;
    border-radius: 50px;
    font-size: 17px;
    font-weight: 700;
    box-shadow: 0 8px 20px rgba(57, 181, 74, 0.35);
    cursor: pointer;
    transition: all 0.2s;

    &:active {
        transform: scale(0.96);
        box-shadow: 0 4px 12px rgba(57, 181, 74, 0.4);
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
    }
}

.success-popup .popup-divider {
    margin: 20px 0 16px;
}

.success-popup .task-card {
    background: #fff5f0;
}

/* 打款成功弹窗：白色卡片 + 绿色金额 + 橙色主按钮 */
.paid-success-popup {
    background: #fff;
    border-radius: 24px;
    width: 100%;
    max-width: 272px;
    padding: 28px 15px;
    text-align: center;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
    position: relative;
    animation: successPop 0.4s ease;
    color: #333;
}

.paid-success-icon-wrap {
    width: 80px;
    height: 80px;
    margin: 0 auto 16px;
    background: rgba(57, 181, 74, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.paid-success-icon {
    width: 70px;
    height: 70px;
    animation: successCheck 0.6s ease;
}

.paid-success-icon-svg {
    width: 100%;
    height: 100%;
    display: block;
}

.paid-success-icon-circle {
    fill: #39b54a;
}

.paid-success-icon-check {
    fill: none;
    stroke: #fff;
    stroke-width: 5;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.paid-success-title {
    font-size: 18px;
    font-weight: 700;
    color: #333;
    margin-bottom: 12px;
}

.paid-success-amount-container {
    display: flex;
    align-items: baseline;
    justify-content: center;
    margin-bottom: 8px;
    color: #39b54a;
    font-weight: 700;
}

.paid-success-amount-plus {
    font-size: 22px;
    margin-right: 2px;
    font-weight: 600;
}

.paid-success-amount-value {
    font-size: 44px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: -1px;
}

.paid-success-amount-unit {
    font-size: 18px;
    margin-left: 2px;
    font-weight: 600;
}

.paid-success-desc {
    color: #999;
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 20px;
}

.paid-success-btn {
    width: 100%;
    height: 48px;
    background: #ff5a33;
    color: #fff;
    border: none;
    border-radius: 50px;
    font-size: 17px;
    font-weight: 700;
    box-shadow: 0 8px 20px rgba(255, 90, 51, 0.35);
    cursor: pointer;
    transition: all 0.2s;

    &:active {
        transform: scale(0.96);
        box-shadow: 0 4px 12px rgba(255, 90, 51, 0.4);
    }
}

.paid-success-popup .popup-divider {
    margin: 20px 0 16px;
}

.paid-success-popup .task-card {
    background: #fff5f0;
}

@keyframes successPop {
    0% {
        transform: scale(0.7);
        opacity: 0;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

/* 悬浮窗 - 优化为更像小红包的样子（红色+金边+立体感+纹理） */
.floating-red-packet {
    position: fixed;
    right: 16px;
    bottom: 200px; /* 避开tabbar */
    z-index: 2500;
    cursor: pointer;
    animation: floatAnim 2.8s ease-in-out infinite;
}

@keyframes floatAnim {
    0%, 100% {
        transform: translateY(0) rotate(-2deg);
    }
    50% {
        transform: translateY(-10px) rotate(2deg);
    }
}

.float-packet-img {
    display: block;
    width: 68px;
    height: auto;
}

/* 响应式优化 - 移动端适配 */
@media (max-width: 375px) {
    .popup-container {
        padding: 10px;
    }
}
</style>
